import { Link } from '@tanstack/react-router';
import {
    ArrowLeft,
    Bot,
    ExternalLink,
    MessageSquare,
    MoreHorizontal,
    PanelRight,
    Search,
} from 'lucide-react';
import {
    type ComponentProps,
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { CaregiverStatusBadge } from '@apps/web/components/shared/messages/caregiver_status_badge';
import { ConversationAssigneePicker } from '@apps/web/components/shared/messages/conversation_assignment';
import { ConversationSearchBar } from '@apps/web/components/shared/messages/conversation_search_bar';
import { type MessageComposerSurface } from '@apps/web/components/shared/messages/message_composer';
import { MessagesPane } from '@apps/web/components/shared/messages/messages_pane';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { ClickToCallButton } from '@apps/web/components/voice_calls/click_to_call_dialog';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationConversationRealtime } from '@apps/web/hooks/realtime_hooks';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { readConversationOwnership } from '@apps/web/swr_data_fetching/conversation_ownership';
import {
    useCaregiverAgentRunForConversation,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

type ConversationDetailPaneProps = {
    readonly organizationId: string;
    readonly conversationId: string | null;
    readonly highlightMessageId?: string | null;
    readonly selectedConversation?: OrganizationConversationMetadata;
    readonly markAsRead?: (conversationId: string) => Promise<void> | void;
    readonly markAsUnread: (conversationId: string) => Promise<void>;
    readonly onBack?: () => void;
    readonly externalLinkHref?: string | null;
    readonly externalLinkLabel?: string | null;
    readonly onOpenInPhoebe?: () => void;
    // When set, the caregiver name opens this callback instead of navigating
    // to `/caregivers`. The inbox uses this to open the profile modal.
    readonly onOpenCaregiverProfile?: (caregiverId: string) => void;
    readonly messageAnalyticsSurface?: MessageComposerSurface;
    // Conversation ownership (flag `conversation_ownership_enabled`).
    readonly ownershipEnabled?: boolean;
    readonly onAssignConversation?: (
        conversationId: string,
        assignedToUserId: string | null,
    ) => void;
    // Contact Activity Timeline panel toggle (ownership-gated). State lives one
    // level up so the timeline can render as a sibling third column.
    readonly activityPanelOpen?: boolean;
    readonly onToggleActivityPanel?: () => void;
    // Stretch MessagesPane to the thread column instead of the
    // 67rem / 680px reading caps.
    readonly fillThreadWidth?: boolean;
    // Optional seed for MessagesPane instead of fetching.
    readonly overrideMessages?: ComponentProps<
        typeof MessagesPane
    >['overrideMessages'];
};

export function OrganizationConversationDetailPane({
    organizationId,
    conversationId,
    highlightMessageId,
    selectedConversation,
    markAsRead,
    markAsUnread,
    onBack,
    externalLinkHref,
    externalLinkLabel,
    onOpenInPhoebe,
    onOpenCaregiverProfile,
    messageAnalyticsSurface,
    ownershipEnabled = false,
    onAssignConversation,
    activityPanelOpen = false,
    onToggleActivityPanel,
    fillThreadWidth = false,
    overrideMessages,
}: ConversationDetailPaneProps) {
    const { formatName } = useFormatPersonName();
    const { timezone } = useOrganizationTimezone();
    const { isInTrainingMode: smsTrainingReview } = useTrainingMode({
        organizationId,
        reason: 'sms-training-review',
    });
    const { data: user } = useUser({
        reason: 'organization-conversation-detail-pane',
    });
    // The caregiver agent keeps one ephemeral run per conversation, so its
    // trace belongs to the conversation rather than to any single message.
    const { caregiverAgentRunId } = useCaregiverAgentRunForConversation(
        conversationId,
        Boolean(user?.admin),
    );
    const lastMarkedConversationIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!conversationId) {
            lastMarkedConversationIdRef.current = null;
            return;
        }
        if (
            !organizationId ||
            lastMarkedConversationIdRef.current === conversationId
        ) {
            return;
        }

        lastMarkedConversationIdRef.current = conversationId;
        void Promise.resolve(markAsRead?.(conversationId)).catch((error) => {
            if (lastMarkedConversationIdRef.current === conversationId) {
                lastMarkedConversationIdRef.current = null;
            }
            console.error('Failed to mark conversation as read', error);
        });
    }, [conversationId, markAsRead, organizationId]);

    const { conversation: fetchedConversation } =
        useOrganizationConversationRealtime(
            selectedConversation ? null : conversationId,
            {
                autoUpdate: true,
                markAsRead: false,
                organizationId,
            },
        );
    const conversationMetadata = selectedConversation ?? fetchedConversation;
    const ownership = conversationMetadata
        ? readConversationOwnership(conversationMetadata)
        : null;
    const isUnread = (conversationMetadata?.unread_count ?? 0) > 0;
    // The ⋯ trigger is only worth its place when something is behind it. An
    // empty menu that opens onto nothing is worse than no menu.
    const conversationTitle = conversationMetadata?.caregiver_name
        ? formatName(conversationMetadata.caregiver_name)
        : (conversationMetadata?.external_contact_name ??
          conversationMetadata?.phone_number);
    const caregiverProfileName = conversationMetadata?.caregiver_name;
    const caregiverProfileId = caregiverProfileName
        ? conversationMetadata?.caregiver_id
        : null;

    // In-conversation keyword search ("search this chat"). The active match is
    // tracked by message id (not index) so it stays put as older pages stream
    // in and prepend earlier matches.
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMatchIds, setSearchMatchIds] = useState<Array<string>>([]);
    const [activeMatchId, setActiveMatchId] = useState<string | null>(null);

    // Reset search synchronously when the selected conversation changes (the
    // "adjust state during render" pattern) so the new thread never renders a
    // frame carrying the previous conversation's keyword, highlights, counts,
    // or auto-load.
    const [searchConversationId, setSearchConversationId] =
        useState(conversationId);
    if (conversationId !== searchConversationId) {
        setSearchConversationId(conversationId);
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchMatchIds([]);
        setActiveMatchId(null);
    }

    const matchCount = searchMatchIds.length;
    // Resolve the active match synchronously: keep the user's selection while
    // it is still a match, otherwise fall back to the most recent match. This
    // avoids a stale id (left over from a prior keyword) driving the highlight
    // ring, scroll, navigation, or counter for a frame before an effect runs.
    const effectiveActiveMatchId =
        activeMatchId && searchMatchIds.includes(activeMatchId)
            ? activeMatchId
            : (searchMatchIds[searchMatchIds.length - 1] ?? null);
    const activeMatchIndex = effectiveActiveMatchId
        ? searchMatchIds.indexOf(effectiveActiveMatchId)
        : -1;

    const handleSearchMatchIdsChange = useCallback((ids: Array<string>) => {
        setSearchMatchIds(ids);
    }, []);

    const goToMatchOffset = useCallback(
        (offset: number) => {
            if (searchMatchIds.length === 0) {
                return;
            }
            const currentIndex = effectiveActiveMatchId
                ? searchMatchIds.indexOf(effectiveActiveMatchId)
                : searchMatchIds.length - 1;
            const nextIndex =
                (currentIndex + offset + searchMatchIds.length) %
                searchMatchIds.length;
            setActiveMatchId(searchMatchIds[nextIndex] ?? null);
        },
        [effectiveActiveMatchId, searchMatchIds],
    );

    const handleNextMatch = useCallback(
        () => goToMatchOffset(1),
        [goToMatchOffset],
    );
    const handlePreviousMatch = useCallback(
        () => goToMatchOffset(-1),
        [goToMatchOffset],
    );

    const handleCloseSearch = useCallback(() => {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchMatchIds([]);
        setActiveMatchId(null);
    }, []);

    const activeSearchMatchId = isSearchOpen ? effectiveActiveMatchId : null;

    const handleMarkRead = useCallback(async () => {
        if (!conversationId || !markAsRead) {
            return;
        }

        try {
            await markAsRead(conversationId);
            toast.success('Marked as read', { persistNotification: false });
        } catch (error) {
            console.error('Failed to mark conversation as read', error);
            toast.error('Failed to mark as read', {
                persistNotification: false,
            });
        }
    }, [conversationId, markAsRead]);

    const handleMarkUnread = useCallback(async () => {
        if (!conversationId) {
            return;
        }

        try {
            await markAsUnread(conversationId);
            toast.success('Marked as unread', { persistNotification: false });
        } catch (error) {
            console.error('Failed to mark conversation as unread', error);
            toast.error('Failed to mark as unread', {
                persistNotification: false,
            });
        }
    }, [conversationId, markAsUnread]);

    if (!conversationId) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-center">
                    <MessageSquare className="text-muted-foreground/50 mx-auto mb-3 h-12 w-12" />
                    <p className="text-muted-foreground">
                        Select a conversation to view messages
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex h-full min-h-0 flex-col',
                fillThreadWidth && 'min-w-0 w-full flex-1',
            )}
        >
            {conversationMetadata ? (
                <div className="bg-background flex-shrink-0 border-b px-4 py-3 md:py-[7.5px]">
                    {/* 7.5 + the 32px controls + 7.5 + the 1px border is 48px,
                        the chat panel header's h-12, so the two border lines
                        and the two icon rows meet across the panel divider. A
                        fixed height would clip this row where it wraps, which
                        it does once the pane drops under ~300px. Touch sizes
                        keep the roomier py-3 below md, where the panels never
                        sit side by side. */}
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 basis-48">
                            {onBack ? (
                                <Button
                                    className="mb-2 -ml-2 h-8 px-2"
                                    onClick={onBack}
                                    size="sm"
                                    type="button"
                                    variant="ghost"
                                >
                                    <ArrowLeft className="mr-1 h-4 w-4" />
                                    Back
                                </Button>
                            ) : null}
                            <div className="flex min-w-0 items-center gap-2">
                                {/* truncate on the heading alone clipped the
                                    name mid-word, because its child is a
                                    button or link: an inline-block child is
                                    cut without an ellipsis. Both carry it. */}
                                <h2 className="min-w-0 truncate text-lg font-semibold">
                                    {caregiverProfileName &&
                                    caregiverProfileId ? (
                                        onOpenCaregiverProfile ? (
                                            <button
                                                className="focus-visible:ring-ring block max-w-full truncate rounded-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                                onClick={() =>
                                                    onOpenCaregiverProfile(
                                                        caregiverProfileId,
                                                    )
                                                }
                                                type="button"
                                            >
                                                {formatName(
                                                    caregiverProfileName,
                                                )}
                                            </button>
                                        ) : (
                                            <Link
                                                className="focus-visible:ring-ring block max-w-full truncate rounded-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                                search={(previous) => ({
                                                    organizationId,
                                                    mode:
                                                        previous.mode ===
                                                            'live' ||
                                                        previous.mode ===
                                                            'sandbox'
                                                            ? previous.mode
                                                            : undefined,
                                                    caregiverId:
                                                        caregiverProfileId,
                                                })}
                                                to="/caregivers"
                                            >
                                                {formatName(
                                                    caregiverProfileName,
                                                )}
                                            </Link>
                                        )
                                    ) : (
                                        conversationTitle
                                    )}
                                </h2>
                                <CaregiverStatusBadge
                                    status={
                                        conversationMetadata.caregiver_status
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center">
                            {ownershipEnabled &&
                            conversationMetadata &&
                            onAssignConversation ? (
                                <ConversationAssigneePicker
                                    organizationId={organizationId}
                                    conversationId={conversationMetadata.id}
                                    assignedToUserId={
                                        ownership?.assigned_to_user_id ?? null
                                    }
                                    assignedToUserName={
                                        ownership?.assigned_to_user_name ?? null
                                    }
                                    onAssign={(userId) =>
                                        onAssignConversation(
                                            conversationMetadata.id,
                                            userId,
                                        )
                                    }
                                />
                            ) : null}
                            {conversationMetadata.caregiver_id ? (
                                <ClickToCallButton
                                    organizationId={organizationId}
                                    caregiverId={
                                        conversationMetadata.caregiver_id
                                    }
                                    caregiverName={
                                        conversationMetadata.caregiver_name ??
                                        null
                                    }
                                    iconOnly={true}
                                />
                            ) : null}
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <Button
                                        aria-label="Search this chat"
                                        className="h-11 w-11 md:h-8 md:w-8"
                                        onClick={() =>
                                            setIsSearchOpen((open) => !open)
                                        }
                                        size="icon"
                                        type="button"
                                        variant={
                                            isSearchOpen ? 'secondary' : 'ghost'
                                        }
                                    >
                                        <Search className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    Search this chat
                                </TooltipContent>
                            </Tooltip>
                            {ownershipEnabled && onToggleActivityPanel ? (
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <Button
                                            aria-label="Activity timeline"
                                            className="h-11 w-11 md:h-8 md:w-8"
                                            onClick={onToggleActivityPanel}
                                            size="icon"
                                            type="button"
                                            variant={
                                                activityPanelOpen
                                                    ? 'secondary'
                                                    : 'ghost'
                                            }
                                        >
                                            <PanelRight className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {activityPanelOpen
                                            ? 'Hide activity timeline'
                                            : 'Activity timeline'}
                                    </TooltipContent>
                                </Tooltip>
                            ) : null}
                            <DropdownMenu>
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <DropdownMenuTrigger asChild={true}>
                                            <Button
                                                className="h-11 w-11 md:h-8 md:w-8"
                                                size="icon"
                                                variant="ghost"
                                            >
                                                <span className="sr-only">
                                                    Conversation actions
                                                </span>
                                                <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Conversation actions
                                    </TooltipContent>
                                </Tooltip>
                                <DropdownMenuContent align="end">
                                    {isUnread ? (
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                void handleMarkRead();
                                            }}
                                        >
                                            Mark as read
                                        </DropdownMenuItem>
                                    ) : (
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                void handleMarkUnread();
                                            }}
                                        >
                                            Mark as unread
                                        </DropdownMenuItem>
                                    )}
                                    {externalLinkHref && externalLinkLabel ? (
                                        <Fragment>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild={true}>
                                                <a
                                                    href={externalLinkHref}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    {externalLinkLabel}
                                                </a>
                                            </DropdownMenuItem>
                                        </Fragment>
                                    ) : null}
                                    {onOpenInPhoebe ? (
                                        <DropdownMenuItem
                                            onSelect={() => onOpenInPhoebe()}
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                            Open in Phoebe
                                        </DropdownMenuItem>
                                    ) : null}
                                    {user?.admin && caregiverAgentRunId ? (
                                        <Fragment>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild={true}>
                                                <Link
                                                    to="/admin/agent-traces/run/$runId"
                                                    params={{
                                                        runId: caregiverAgentRunId,
                                                    }}
                                                >
                                                    <Bot className="h-4 w-4" />
                                                    Agent trace
                                                </Link>
                                            </DropdownMenuItem>
                                        </Fragment>
                                    ) : null}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                    {isSearchOpen ? (
                        <div className="mt-3">
                            <ConversationSearchBar
                                activeMatchIndex={activeMatchIndex}
                                matchCount={matchCount}
                                onClose={handleCloseSearch}
                                onNextMatch={handleNextMatch}
                                onPreviousMatch={handlePreviousMatch}
                                onQueryChange={setSearchQuery}
                                query={searchQuery}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            <div className="min-h-0 flex-1">
                <MessagesPane
                    activeSearchMatchId={activeSearchMatchId}
                    caregiverName={conversationMetadata?.caregiver_name ?? null}
                    conversationId={conversationId}
                    fillThreadWidth={fillThreadWidth}
                    highlightMessageId={highlightMessageId}
                    highlightLabel="Internal note"
                    markAsRead={false}
                    messageAnalyticsSurface={messageAnalyticsSurface}
                    onSearchMatchIdsChange={handleSearchMatchIdsChange}
                    organizationId={organizationId}
                    refreshOnOpen={true}
                    searchActive={isSearchOpen}
                    searchQuery={searchQuery}
                    showComposer={true}
                    showAutomatedReplyControls={true}
                    showConversationAuditEvents={ownershipEnabled}
                    showMessageNotes={true}
                    showRating={false}
                    smsTrainingReview={smsTrainingReview}
                    timezone={timezone}
                    overrideMessages={overrideMessages}
                />
            </div>
        </div>
    );
}
